import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Droplets, BrickWall, CarFront, Sun, Waves, TrendingDown, ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { PROBLEMS, prefillService } from "@/lib/site-data";

const ICONS = [Droplets, BrickWall, CarFront, Sun, Waves, TrendingDown];

export default function ProblemSelector() {
  const [active, setActive] = useState(null);

  return (
    <section id="problems" data-testid="problem-section" className="bg-black py-28 md:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Reveal>
          <Overline>Start Here</Overline>
          <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mb-4">
            What Problem Are You Dealing With?
          </h2>
          <p className="text-gray-400 max-w-xl mb-14">
            Pick the issue that's bothering you — we'll point you to the right fix.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROBLEMS.map((p, i) => {
            const Icon = ICONS[i];
            const isActive = active === p.id;
            return (
              <Reveal key={p.id} delay={0.05 * i}>
                <button
                  data-testid={`problem-tile-${p.id}`}
                  onClick={() => setActive(isActive ? null : p.id)}
                  className={`w-full text-left rounded-2xl bg-[#111827] p-7 border-2 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 ${
                    isActive
                      ? "border-[#0066FF] shadow-[0_0_40px_rgba(0,102,255,0.35)]"
                      : "border-white/10 hover:border-cyan-400/40 hover:shadow-[0_20px_50px_rgba(0,102,255,0.15)]"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <span className={`inline-flex rounded-xl p-3 transition-colors duration-300 ${isActive ? "bg-[#0066FF] text-white" : "bg-[#0066FF]/15 text-cyan-400"}`}>
                      <Icon size={24} />
                    </span>
                    <ArrowUpRight size={20} className={`transition-colors duration-300 ${isActive ? "text-cyan-400" : "text-gray-600"}`} />
                  </div>
                  <h3 className="font-display text-xl font-semibold mt-5 text-white">{p.title}</h3>
                  <p className="text-sm text-gray-400 mt-2">{p.desc}</p>
                </button>
              </Reveal>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          {active && (
            <motion.div
              key={active}
              data-testid="problem-recommendation-panel"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 rounded-2xl bg-[#0A0A0A] border border-white/10 text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 relative overflow-hidden"
            >
              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#0066FF]/25 blur-3xl" />
              <div className="relative">
                <span className="text-xs uppercase tracking-[0.25em] font-bold text-cyan-400">Our Recommendation</span>
                <h3 className="font-display text-3xl font-semibold mt-2">
                  {PROBLEMS.find((p) => p.id === active)?.service}
                </h3>
                <p className="text-gray-400 mt-2 max-w-lg">
                  That's exactly the kind of problem we solve every week. Tell us what's going on and
                  we'll come take a look — the estimate is free.
                </p>
              </div>
              <a
                href="#estimate"
                data-testid="problem-get-fixed-btn"
                onClick={() => prefillService(PROBLEMS.find((p) => p.id === active)?.service)}
                className="relative ml-auto inline-flex shrink-0 items-center gap-2 rounded-md bg-[#00E5FF] px-7 py-4 font-display text-sm font-semibold tracking-widest text-black transition-shadow duration-300 hover:shadow-[0_0_30px_rgba(0,229,255,0.5)]"
              >
                GET THIS FIXED <ArrowRight size={16} />
              </a>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
